import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Home } from "lucide-react";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import logo from "@/assets/logo-novo.png";

const heroImageMap: Record<string, [string, string]> = {
  "/o-miejscu": ["/images/heroes/hero-o-miejscu.webp", "/images/heroes/hero-o-miejscu-mobile.webp"],
};

const preloadHeroImage = (href: string) => {
  const pair = heroImageMap[href];
  if (!pair) return;
  const isMobile = window.innerWidth < 768;
  const url = isMobile ? pair[1] : pair[0];
  if (document.querySelector(`link[href="${url}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  document.head.appendChild(link);
};

type NavItem = {
  label: string;
  href: string;
};

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const prefetchMap: Record<string, () => void> = {
    "/o-miejscu": () => { import("@/pages/OMiejscu"); },
    "/wynajem-studia": () => { import("@/pages/Galeria"); },
    "/sesje": () => { import("@/pages/SesjeBiznesowe"); },
    "/cennik": () => { import("@/pages/Cennik"); },
    "/kontakt": () => { import("@/pages/Kontakt"); },
  };

  const prefetchRoute = (href: string) => {
    prefetchMap[href]?.();
    preloadHeroImage(href);
  };

  const navItems: NavItem[] = [
    { label: "Studio", href: "/o-miejscu" },
    { label: "Wynajem studia", href: "/wynajem-studia" },
    { label: "Sesje", href: "/sesje" },
    { label: "Cennik", href: "/cennik" },
    { label: "Kontakt", href: "/kontakt" },
  ];

  const isActive = (href: string) => 
    href === "/" ? location.pathname === "/" : location.pathname.startsWith(href);

  const liquidEasing: [number, number, number, number] = reduceMotion 
    ? [0.4, 0, 0.2, 1] 
    : [0.25, 0.46, 0.45, 0.94];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          
          {/* Desktop Floating Pill */}
          <motion.div
            className="hidden lg:flex items-center rounded-full p-2 gap-1.5"
            initial={false}
            animate={{
              backgroundColor: scrolled 
                ? "rgba(10, 10, 11, 0.7)" 
                : "transparent",
              borderColor: scrolled 
                ? "rgba(255, 255, 255, 0.08)" 
                : "transparent",
              boxShadow: scrolled 
                ? "0 8px 32px rgba(0, 0, 0, 0.4)" 
                : "none",
              backdropFilter: scrolled 
                ? "blur(24px) saturate(180%)" 
                : "blur(0px)",
            }}
            transition={{ duration: 0.5, ease: liquidEasing }}
            style={{
              border: "1px solid",
              WebkitBackdropFilter: scrolled ? "blur(24px) saturate(180%)" : "blur(0px)",
            }}
          >
            {/* Home */}
            <Link 
              to="/" 
              className="relative flex items-center justify-center px-3 py-2.5"
              aria-label="Strona główna"
              onMouseEnter={() => setHoveredItem("home")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <Home 
                size={20} 
                className="relative z-10 transition-colors duration-200"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              />
              <motion.span
                className="absolute bottom-1 left-1/2 h-[2px] rounded-full bg-primary"
                initial={false}
                animate={{
                  width: hoveredItem === "home" || (location.pathname === "/" && !hoveredItem) ? 16 : 0,
                  x: "-50%",
                }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                style={{ boxShadow: "0 0 8px hsl(var(--primary)), 0 0 16px hsl(var(--primary) / 0.5)" }}
              />
            </Link>

            {/* Separator */}
            <motion.div 
              className="h-6 w-px mx-2"
              initial={false}
              animate={{ backgroundColor: scrolled ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.15)" }}
              transition={{ duration: 0.5, ease: liquidEasing }}
            />

            {/* Text Logo */}
            <Link to="/" className="flex items-center px-4 py-1">
              <img src={logo} alt="Studio Skandal" className="h-[36px] w-auto" width="180" height="36" style={{ transform: 'translateY(5px)' }} />
            </Link>

            {/* Separator */}
            <motion.div 
              className="h-6 w-px mx-2"
              initial={false}
              animate={{ backgroundColor: scrolled ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.15)" }}
              transition={{ duration: 0.5, ease: liquidEasing }}
            />

            {/* Nav Links */}
            <div className="flex items-center gap-0.5">
              {navItems.map((item) => {
                const active = isActive(item.href);
                const showUnderline = hoveredItem === item.href || (active && !hoveredItem && location.pathname !== "/");
                
                if (item.href.startsWith("http")) {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative px-4 py-2.5 text-sm font-medium"
                      onMouseEnter={() => setHoveredItem(item.href)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <motion.span
                        className="relative z-10 whitespace-nowrap"
                        initial={false}
                        animate={{ color: "rgba(255, 255, 255, 0.9)" }}
                        transition={{ duration: 0.2, ease: liquidEasing }}
                      >
                        {item.label}
                      </motion.span>
                      <motion.span
                        className="absolute bottom-1 left-1/2 h-[2px] rounded-full bg-primary"
                        initial={false}
                        animate={{
                          width: hoveredItem === item.href ? "70%" : "0%",
                          x: "-50%",
                        }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        style={{ boxShadow: "0 0 8px hsl(var(--primary)), 0 0 16px hsl(var(--primary) / 0.5)" }}
                      />
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="relative px-4 py-2.5 text-sm font-medium"
                    onMouseEnter={() => { setHoveredItem(item.href); prefetchRoute(item.href); }}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <motion.span
                      className="relative z-10 whitespace-nowrap"
                      initial={false}
                      animate={{ color: "rgba(255, 255, 255, 0.9)" }}
                      transition={{ duration: 0.2, ease: liquidEasing }}
                    >
                      {item.label}
                    </motion.span>
                    
                    <motion.span
                      className="absolute bottom-1 left-1/2 h-[2px] rounded-full bg-primary"
                      initial={false}
                      animate={{
                        width: showUnderline ? "70%" : "0%",
                        x: "-50%",
                      }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      style={{ boxShadow: "0 0 8px hsl(var(--primary)), 0 0 16px hsl(var(--primary) / 0.5)" }}
                    />
                  </Link>
                );
              })}
            </div>
          </motion.div>

          {/* Mobile */}
          <motion.div 
            className="lg:hidden flex items-center justify-between w-full px-3 py-2 rounded-full"
            initial={false}
            animate={{
              backgroundColor: (scrolled || mobileMenuOpen)
                ? "rgba(10, 10, 11, 0.85)" 
                : "transparent",
              borderColor: (scrolled || mobileMenuOpen)
                ? "rgba(255, 255, 255, 0.08)" 
                : "transparent",
              boxShadow: (scrolled || mobileMenuOpen)
                ? "0 8px 32px rgba(0, 0, 0, 0.4)" 
                : "none",
              backdropFilter: (scrolled || mobileMenuOpen)
                ? "blur(8px)" 
                : "blur(0px)",
            }}
            transition={{ duration: 0.5, ease: liquidEasing }}
            style={{
              border: "1px solid",
              WebkitBackdropFilter: (scrolled || mobileMenuOpen) ? "blur(8px)" : "blur(0px)",
            }}
          >
            <Link to="/" className={`relative z-10 ml-2 transition-opacity duration-300 ${scrolled || mobileMenuOpen || location.pathname !== '/' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <img src={logo} alt="Studio Skandal" className="h-[36px] w-auto" width="180" height="36" style={{ transform: 'translateY(5px)' }} />
            </Link>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              className="relative z-10 p-2.5 rounded-full text-white active:scale-95 transition-transform duration-100"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </motion.div>
        </div>
      </nav>

      {/* Mobile Backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div
          className="fixed top-0 left-0 right-0 z-50 lg:hidden"
          style={{
            background: "rgba(10,10,11,0.98)",
            boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.5)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          }}
        >
          <nav style={{ marginTop: "80px" }} className="px-6 pb-6 space-y-1">
            <Link 
              to="/" 
              onClick={() => setMobileMenuOpen(false)} 
              className={`relative flex items-center gap-3 px-5 py-3.5 rounded-xl text-base font-medium transition-colors duration-150 ${
                location.pathname === "/" 
                  ? 'text-primary bg-primary/10' 
                  : 'text-white/90 hover:text-white hover:bg-white/5'
              }`}
            >
              <Home size={20} />
              Strona główna
              {location.pathname === "/" && (
                <div 
                  className="absolute bottom-2 left-5 right-5 h-[2px] rounded-full bg-primary"
                  style={{ boxShadow: "0 0 8px hsl(38 40% 60% / 0.6)" }}
                />
              )}
            </Link>

            {navItems.map((item) => {
              const active = isActive(item.href);
              if (item.href.startsWith("http")) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="relative block px-5 py-3.5 rounded-xl text-base font-medium transition-colors duration-150 text-white/90 hover:text-white hover:bg-white/5"
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <Link 
                  key={item.href}
                  to={item.href} 
                  onClick={() => setMobileMenuOpen(false)}
                  onTouchStart={() => prefetchRoute(item.href)}
                  className={`relative block px-5 py-3.5 rounded-xl text-base font-medium transition-colors duration-150 ${
                    active 
                      ? 'text-primary bg-primary/10' 
                      : 'text-white/90 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.label}
                  {active && (
                    <div 
                      className="absolute bottom-2 left-5 right-5 h-[2px] rounded-full bg-primary"
                      style={{ boxShadow: "0 0 8px hsl(38 40% 60% / 0.6)" }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </>
  );
};

export default Navigation;
